import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Tenant context — authed-only. Exposes the current tenant snapshot so the
 * layout shell + any component that cares about branding (logo + accent)
 * can read a single source of truth without each one re-fetching.
 *
 *   tenant        { id, name, logo_url, primary_color, status, locked, ... } | null
 *   refetch()     re-pulls GET /api/tenants/me
 *
 * The accent colour, when present, is also pushed to CSS variables on
 * :root (`--wc-accent`, `--wc-accent-rgb`) so Tailwind arbitrary-value
 * utilities like `bg-[var(--wc-accent)]` can reach it from anywhere.
 */
const TenantContext = createContext({ tenant: null, refetch: async () => {} });

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);

  const refetch = useCallback(async () => {
    if (!user) { setTenant(null); return; }
    try {
      const { data } = await api.get("/tenants/me");
      setTenant(data);
    } catch {
      setTenant(null);
    }
  }, [user]);

  useEffect(() => { refetch(); }, [refetch]);

  // Push accent to CSS variables so other components can `var(--wc-accent)`.
  useEffect(() => {
    const root = document.documentElement;
    const hex = (tenant?.primary_color || "").trim();
    if (hex && /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
      root.style.setProperty("--wc-accent", hex);
      root.style.setProperty("--wc-accent-rgb", hexToRgb(hex));
    } else {
      root.style.removeProperty("--wc-accent");
      root.style.removeProperty("--wc-accent-rgb");
    }
  }, [tenant?.primary_color]);

  return (
    <TenantContext.Provider value={{ tenant, refetch }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => useContext(TenantContext);

/* Convert `#RRGGBB` or `#RGB` → `r, g, b` so we can build rgba() values. */
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const parts = h.length === 3
    ? h.split("").map((c) => c + c)
    : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)];
  const [r, g, b] = parts.map((p) => parseInt(p, 16));
  return `${r}, ${g}, ${b}`;
}

/**
 * Resolve a possibly-relative tenant logo URL to a fully-qualified one
 * suitable for `<img src>`. Backend returns `/api/public/tenants/{id}/logo`
 * for uploaded logos — we need to prefix the backend origin.
 * Pasted external URLs are returned as-is.
 */
export function resolveLogoUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/\/+$/, "") + url;
}
